import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Close as CloseIcon, LockOpen as LockOpenIcon } from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { usePageBreakpoints } from '../../hooks/useClinicPageBreakpoints';

export interface OpenCashRegisterDialogProps {
  open: boolean;
  onClose: () => void;
  /** Chamado depois que o caixa abriu com sucesso — use para repetir o lançamento. */
  onOpened?: () => void | Promise<void>;
}

/**
 * Abre o caixa do operador logado sem sair da tela atual.
 * O `unit_id` vai no header `X-Unit-Id` (axios) ou no body via middleware do backend.
 */
const OpenCashRegisterDialog: React.FC<OpenCashRegisterDialogProps> = ({
  open,
  onClose,
  onOpened,
}) => {
  const { user } = useAuth();
  const { isMobile } = usePageBreakpoints();
  const [openingBalance, setOpeningBalance] = useState(0);
  const [openingNotes, setOpeningNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setOpeningBalance(0);
    setOpeningNotes('');
    setError(null);
    setSubmitting(false);
  }, [open]);

  const handleSubmit = async () => {
    const value = Number(openingBalance);
    if (!Number.isFinite(value) || value < 0) {
      setError('Informe um saldo de abertura válido.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await axios.post(getApiUrl('/financial-module/cash-register/open'), {
        opening_balance: value,
        notes: openingNotes.trim() || undefined,
        user_id: user?.id,
        user_name: user?.full_name,
        unit_id: user?.current_unit?.id || undefined,
      });
      onClose();
      await onOpened?.();
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Não foi possível abrir o caixa.';
      const text = typeof msg === 'string' ? msg : 'Não foi possível abrir o caixa.';
      if (/já possui um caixa aberto/i.test(text)) {
        onClose();
        await onOpened?.();
        return;
      }
      setError(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LockOpenIcon sx={{ color: '#10b981' }} />
          Abrir caixa
        </span>
        <IconButton onClick={onClose} disabled={submitting} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Operador: <strong>{user?.full_name || '—'}</strong>
          {user?.current_unit?.name ? (
            <>
              <br />
              Unidade: <strong>{user.current_unit.name}</strong>
            </>
          ) : null}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Saldo de abertura"
          type="number"
          value={openingBalance}
          onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
          InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
          inputProps={{ min: 0, step: 0.01 }}
          sx={{ mb: 2 }}
          autoFocus
        />
        <TextField
          fullWidth
          label="Observações (opcional)"
          multiline
          minRows={2}
          value={openingNotes}
          onChange={(e) => setOpeningNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={submitting}
          sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
        >
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Abrir caixa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OpenCashRegisterDialog;
